import React, { useEffect, useState, useCallback } from 'react';
import { Users, Check, X } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Avatar from '../../components/common/Avatar';
import Badge from '../../components/common/Badge';
import Select from '../../components/common/Select';
import Skeleton from '../../components/common/Skeleton';
import EmptyState from '../../components/common/EmptyState';
import { storeService } from '../../services/storeService';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';

const STATUS = {
  pending: { label: 'Pendente', color: 'warning' },
  active: { label: 'Ativo', color: 'success' },
  suspended: { label: 'Suspenso', color: 'neutral' },
};

const ResellerRow = ({ reseller, onApprove, onReject, onSaveWallet }) => {
  const [editWallet, setEditWallet] = useState(false);
  const [walletId, setWalletId] = useState(reseller.asaasAccountId || '');
  const [saving, setSaving] = useState(false);
  const s = STATUS[reseller.status] || { label: reseller.status, color: 'neutral' };

  const save = async () => {
    setSaving(true);
    try {
      await onSaveWallet(reseller.userId, walletId.trim() || null);
      setEditWallet(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex gap-3 flex-1 min-w-0">
          <Avatar name={reseller.user?.name} size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-app-text">
                {reseller.user?.name || '(sem nome)'}
              </span>
              <Badge color={s.color} variant="soft" size="sm">{s.label}</Badge>
            </div>
            <p className="text-sm text-app-text-secondary">
              {reseller.user?.email}
              {reseller.user?.phone ? ` · ${reseller.user.phone}` : ''}
            </p>

            <div className="mt-3">
              <p className="text-xs font-semibold text-app-text-secondary uppercase tracking-wide mb-1">
                Asaas Wallet (split payment)
              </p>
              {!editWallet ? (
                <div className="flex items-center gap-2 text-sm">
                  {reseller.asaasAccountId ? (
                    <span className="font-mono text-app-text bg-app-surface px-2 py-1 rounded border border-app-border">
                      {reseller.asaasAccountId}
                    </span>
                  ) : (
                    <span className="italic text-app-text-secondary">não cadastrada</span>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setEditWallet(true)}>
                    {reseller.asaasAccountId ? 'Editar' : 'Cadastrar'}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Input
                    value={walletId}
                    onChange={(e) => setWalletId(e.target.value)}
                    placeholder="wallet-id do Asaas"
                    className="flex-1 font-mono text-sm"
                  />
                  <Button size="sm" onClick={save} loading={saving}>
                    Salvar
                  </Button>
                  <Button
                    size="sm"
                    variant="subtle"
                    onClick={() => {
                      setEditWallet(false);
                      setWalletId(reseller.asaasAccountId || '');
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 md:flex-col md:w-40">
          {reseller.status === 'pending' && (
            <>
              <Button size="sm" fullWidth leftIcon={<Check size={14} />} onClick={() => onApprove(reseller.userId)}>
                Aprovar
              </Button>
              <Button size="sm" variant="subtle" fullWidth leftIcon={<X size={14} />} onClick={() => onReject(reseller.userId)}>
                Rejeitar
              </Button>
            </>
          )}
          {reseller.status === 'active' && (
            <Button size="sm" variant="danger" fullWidth onClick={() => onReject(reseller.userId)}>
              Suspender
            </Button>
          )}
          {reseller.status === 'suspended' && (
            <Button size="sm" fullWidth onClick={() => onApprove(reseller.userId)}>
              Reativar
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

const ResellersManagement = () => {
  const { currentStoreId } = useAuth();
  const { error, success } = useToast();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentStoreId) return;
    setLoading(true);
    try {
      const data = await storeService.listResellers(currentStoreId, { status: filter || undefined });
      setRows(data || []);
    } catch (e) {
      error(e.message || 'Erro ao carregar revendedores');
    } finally {
      setLoading(false);
    }
  }, [currentStoreId, filter, error]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (userId) => {
    try {
      await storeService.approveReseller(currentStoreId, userId);
      success('Revendedor aprovado');
      load();
    } catch (e) { error(e.message); }
  };

  const reject = async (userId) => {
    try {
      await storeService.rejectReseller(currentStoreId, userId);
      success('Revendedor suspenso');
      load();
    } catch (e) { error(e.message); }
  };

  const saveWallet = async (userId, walletId) => {
    try {
      await storeService.updateResellerAsaasAccount(currentStoreId, userId, walletId);
      success(walletId ? 'Wallet cadastrada' : 'Wallet removida');
      load();
    } catch (e) { error(e.message); }
  };

  if (!currentStoreId) {
    return <p className="text-app-text-secondary">Selecione uma loja primeiro.</p>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-app-text">Revendedores</h1>
          <p className="text-sm text-app-text-secondary mt-1">
            Aprove candidaturas e cadastre a wallet Asaas de cada revendedor pra habilitar split payment.
          </p>
        </div>
        <div className="w-48">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            options={[
              { value: '', label: 'Todos' },
              { value: 'pending', label: 'Pendentes' },
              { value: 'active', label: 'Ativos' },
              { value: 'suspended', label: 'Suspensos' },
            ]}
          />
        </div>
      </div>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="block" height={120} />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card>
          <EmptyState
            icon={Users}
            title="Nenhum revendedor ainda"
            description={
              filter === 'pending'
                ? 'Quando alguém se candidatar, a solicitação aparece aqui.'
                : 'Quando houver candidaturas aprovadas, a lista aparece aqui.'
            }
          />
        </Card>
      )}

      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.id}>
            <ResellerRow
              reseller={r}
              onApprove={approve}
              onReject={reject}
              onSaveWallet={saveWallet}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ResellersManagement;
